<template>
  <b-modal
    v-model="isVisible"
    title="Detalhes da Execução"
    size="xl"
    @hidden="onHidden"
  >
    <template #modal-header="{ close }">
      <h5 class="modal-title">
        <i class="fa fa-info-circle"></i> Detalhes da Sincronização
      </h5>
      <button type="button" class="close" aria-label="Close" @click="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </template>

    <div v-if="loading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fa-3x text-muted"></i>
      <p class="mt-3 text-muted">Carregando detalhes...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      <i class="fa fa-exclamation-triangle"></i> {{ error }}
    </div>

    <div v-else-if="execution">
      <!-- Informações Gerais -->
      <div class="card mb-3">
        <div class="card-header bg-primary text-white">
          <span><i class="fa fa-info"></i> Informações Gerais</span>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-3">
              <div class="info-item">
                <label>Data/Hora:</label>
                <div>{{ formatDateTime(execution.startTime) }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-item">
                <label>Duração:</label>
                <div class="font-weight-bold">{{ formatDuration(execution.duration) }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-item">
                <label>Iniciada Por:</label>
                <div class="font-weight-bold">{{ execution.triggeredBy }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-item">
                <label>Total de Usuários:</label>
                <div class="font-weight-bold text-primary" style="font-size: 1.5rem;">
                  {{ execution.totalUsers }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Resumo de Resultados -->
      <div class="card mb-3">
        <div class="card-header bg-info text-white">
          <i class="fa fa-bar-chart"></i> Resumo de Resultados
        </div>
        <div class="card-body">
          <div class="row text-center">
            <div class="col-md-4">
              <div class="card bg-success text-white metric-card">
                <div class="card-body">
                  <h2 class="mb-2">{{ execution.createdUsers }}</h2>
                  <p class="mb-0"><i class="fa fa-plus-circle"></i> Criados</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card bg-info text-white metric-card">
                <div class="card-body">
                  <h2 class="mb-2">{{ execution.updatedUsers }}</h2>
                  <p class="mb-0"><i class="fa fa-sync"></i> Atualizados</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card bg-danger text-white metric-card">
                <div class="card-body">
                  <h2 class="mb-2">{{ execution.failedUsers }}</h2>
                  <p class="mb-0"><i class="fa fa-times-circle"></i> Falhas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs com Detalhes -->
      <b-tabs content-class="mt-3" class="custom-tabs">
        <!-- Tab: Todos os Usuários -->
        <b-tab title="Todos os Usuários" active>
          <template #title>
            <i class="fa fa-users"></i> Todos ({{ execution.results.length }})
          </template>
          <UserResultsTable
            :results="execution.results"
            :show-all="true"
          />
        </b-tab>

        <!-- Tab: Falhas -->
        <b-tab v-if="failedResults.length > 0" title="Falhas">
          <template #title>
            <i class="fa fa-exclamation-triangle text-danger"></i> Falhas ({{ failedResults.length }})
          </template>
          <UserResultsTable
            :results="failedResults"
            :highlight-errors="true"
          />
        </b-tab>

        <!-- Tab: Criados -->
        <b-tab v-if="createdResults.length > 0" title="Criados">
          <template #title>
            <i class="fa fa-plus-circle text-success"></i> Criados ({{ createdResults.length }})
          </template>
          <UserResultsTable :results="createdResults" />
        </b-tab>

        <!-- Tab: Atualizados -->
        <b-tab v-if="updatedResults.length > 0" title="Atualizados">
          <template #title>
            <i class="fa fa-sync text-info"></i> Atualizados ({{ updatedResults.length }})
          </template>
          <UserResultsTable :results="updatedResults" />
        </b-tab>
      </b-tabs>
    </div>

    <template #modal-footer="{ close }">
      <button type="button" class="btn btn-secondary" @click="close()">
        <i class="fa fa-times"></i> Fechar
      </button>
    </template>
  </b-modal>
</template>

<script>
import adSyncService from '@/shared/http/repositories/adsync/adsync'
import moment from 'moment'
import UserResultsTable from './UserResultsTable.vue'

export default {
  name: 'ExecutionDetailsModal',
  components: {
    UserResultsTable
  },
  props: {
    executionId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      isVisible: false,
      loading: false,
      error: null,
      execution: null
    }
  },
  computed: {
    failedResults () {
      if (!this.execution || !this.execution.results) return []
      return this.execution.results.filter(r => r.status === 'FAILED')
    },
    createdResults () {
      if (!this.execution || !this.execution.results) return []
      return this.execution.results.filter(r => r.operation === 'CREATE' && r.status === 'COMPLETED')
    },
    updatedResults () {
      if (!this.execution || !this.execution.results) return []
      return this.execution.results.filter(r => r.operation === 'UPDATE' && r.status === 'COMPLETED')
    }
  },
  methods: {
    show () {
      this.isVisible = true
      if (this.executionId) {
        this.loadExecutionDetails()
      }
    },
    hide () {
      this.isVisible = false
    },
    onHidden () {
      this.execution = null
      this.error = null
      this.$emit('hidden')
    },
    async loadExecutionDetails () {
      this.loading = true
      this.error = null

      try {
        const response = await adSyncService.getExecutionDetails(this.executionId)
        this.execution = response.data
      } catch (error) {
        console.error('Erro ao carregar detalhes', error)
        this.error = 'Erro ao carregar detalhes da execução. Verifique se o ID está correto.'
      } finally {
        this.loading = false
      }
    },
    formatDateTime (timestamp) {
      return moment(timestamp).format('DD/MM/YYYY HH:mm:ss')
    },
    formatDuration (duration) {
      if (!duration) return '-'

      // Tentar extrair segundos de diferentes formatos
      let seconds = 0

      if (typeof duration === 'number') {
        seconds = duration
      } else if (typeof duration === 'string') {
        // Formato ISO 8601: PT0.7244608S ou PT1M30S
        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(\d+(?:\.\d+)?)?S/)
        if (match) {
          const hours = parseInt(match[1]) || 0
          const minutes = parseInt(match[2]) || 0
          const secs = parseFloat(match[3]) || 0
          seconds = Math.ceil(hours * 3600 + minutes * 60 + secs) // Arredonda para cima
        }
      } else if (duration.seconds !== undefined) {
        seconds = duration.seconds
      }

      if (!seconds || isNaN(seconds) || seconds < 0) return '-'

      if (seconds < 60) return `${seconds}s`
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}m ${secs}s`
    },
    getModeLabel (mode) {
      const labels = { 'FULL': 'Completa', 'MANUAL': 'Manual', 'INCREMENTAL': 'Incremental' }
      return labels[mode] || mode
    },
    getModeVariant (mode) {
      const variants = { 'FULL': 'primary', 'MANUAL': 'warning', 'INCREMENTAL': 'info' }
      return variants[mode] || 'secondary'
    },
    getStatusLabel (status) {
      const labels = { 'COMPLETED': 'Sucesso', 'PARTIAL': 'Parcial', 'FAILED': 'Falhou' }
      return labels[status] || status
    },
    getStatusVariant (status) {
      const variants = { 'COMPLETED': 'success', 'PARTIAL': 'warning', 'FAILED': 'danger' }
      return variants[status] || 'secondary'
    }
  }
}
</script>

<style scoped>
.info-item {
  margin-bottom: 1rem;
}

.info-item label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #73818f;
  margin-bottom: 0.25rem;
  display: block;
}

.metric-card {
  transition: transform 0.2s ease;
}

.metric-card:hover {
  transform: scale(1.05);
}

.metric-card h2 {
  font-size: 2.5rem;
  font-weight: 800;
}

.custom-tabs >>> .nav-tabs {
  border-bottom: 2px solid #dee2e6;
}

.custom-tabs >>> .nav-link {
  font-weight: 600;
  color: #495057;
  border: none;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
}

.custom-tabs >>> .nav-link:hover {
  border-bottom-color: #17a2b8;
  background-color: transparent;
}

.custom-tabs >>> .nav-link.active {
  color: #007bff;
  border-bottom-color: #007bff;
  background-color: transparent;
}
</style>



// WEBPACK FOOTER //
// src/views/ADSync/components/dashboard/ExecutionDetailsModal.vue